import * as gss from '@stratus/gss-ng-sdk';
import {ConfigSetupState} from '@app/run-planning/config-setup/config-setup.state';
import {cloneDeep, get, groupBy, isEmpty, isArray, keyBy, pick, uniq} from 'lodash';
import {getConfigTitle, IMPLICIT_BCL_CONVERT_CONFIG_NAME} from '@app/run-planning/constants';
import {RunSettingsState} from '@app/run-planning/run-settings/run-settings.state';
import {UNSPECIFIED_INDEX_ADAPTER_KIT, UNSPECIFIED_LIB_PREP_KIT} from '@app/cloud-run-prep/constants';
import {ColumnName} from '@app/run-planning/services/sample-table/sample-table-service';
import { RunPlanningState } from '../run-planning.state';
import { ISubSampleDetail, SubSampleNameUtility } from '../services/sample-table/sub-sample-name-utility';

function omitEmptyArrays(settings: any): any {
  if (!settings) { return settings; }
  const result = {};
  for (const key of Object.keys(settings)) {
    if (isArray(settings[key]) && settings[key].length === 0) {
      continue;
    }
    result[key] = settings[key];
  }
  return result;
}

export class CreateSequencingRunAnalysisConfigurationRequest implements gss.CreateSequencingRunAnalysisConfigurationRequest {
  analysisVersionDefinitionId: string;
  description?: string;
  indexAdapterKitId?: string;
  libraryPrepKitId?: string;
  name?: string;
  sampleSettings?: Array<gss.SampleSettingEntry>;
  settings?: any;

  /**
   * Transform a single config to a single GSS AnalysisConfig request (1:1). Assigns the physical & logical level
   * config values across the sampleSettings.sampleIds within the config
   */
  public static fromConfigSetup(state: ConfigSetupState): gss.CreateSequencingRunAnalysisConfigurationRequest {
    let sampleSettings: gss.SampleSettingEntry[] = [];

    if (state.rawAnalysisVersionDefinition.hasSubSamples) {
      const subSampleColumnNameRegex = /([^_]+)_(\w+)/;
      const subSampleNameSeparator = state.rawAnalysisVersionDefinition.subSampleNameSeparator;

      const assocFieldId = state.rawAnalysisVersionDefinition.settings.subSamplesConfiguration.associationFieldId;
      const sampleDataMap  = groupBy(state.sampleData, assocFieldId);
      const subSampleDefMap = keyBy(state.rawAnalysisVersionDefinition.settings.subSamplesConfiguration.subSampleDefinitions, 'id');
      const sampleSettingsFieldMap = keyBy(state.rawAnalysisVersionDefinition.analysisSampleSettings.fields
        .filter(field => !field.hidden && (field.configurationLevel == null || field.configurationLevel === 'perSample')), 'id');

      for (const pairId in sampleDataMap) {
        const perSampleSettings = { };

        // Initialize perSampleSettings with subSamples.settings
        for (const subSampleDefId of Object.keys(subSampleDefMap)) {
          const subSampleDef = subSampleDefMap[subSampleDefId];
          const indexSequenceFieldName = `${subSampleDefId}_${ColumnName.INDEX_1_SEQUENCE}`;
          // Find if any row for this sample has the index sequence filled in
          const rowsWithSubSampleFilledIn = sampleDataMap[pairId].filter(row => !isEmpty(row[indexSequenceFieldName]));
          const hasSubSampleFilledIn = rowsWithSubSampleFilledIn.length > 0;

          // settings defined in the subsample configuration E.g. {Sample_Type: "DNA"}
          if (hasSubSampleFilledIn) {
            perSampleSettings[subSampleDefId] = cloneDeep(subSampleDef.settings);

            // For each column in the (sample data) row, copy the value to sample-settings
            // Note that the column may apply to all or some sub-samples
            for (const columnName in sampleSettingsFieldMap) {
              const supportedSubSampleIds = get(sampleSettingsFieldMap[columnName], 'settings.supportedSubSampleIds', []);
              const isCommonColumn = isEmpty(supportedSubSampleIds);
              // Assuption: same sample should have same per sample settings values across rows, e.g. Pair_ID
              // Get the first non-empty common setting value from the rows for this sample
              const combinedColumnName = isCommonColumn ? columnName : `${subSampleDefId}_${columnName}`;
              const firstNonEmptyValueIfExist = get(rowsWithSubSampleFilledIn.find(
                x => x[combinedColumnName] !== null && x[combinedColumnName] !== undefined && x[combinedColumnName] !== ''),
                combinedColumnName, undefined);

              if (isCommonColumn || supportedSubSampleIds.includes(subSampleDefId)) {
                perSampleSettings[subSampleDefId][columnName] = firstNonEmptyValueIfExist;
              }
            }
          }
        }

        for (const subSampleDefId of Object.keys(perSampleSettings)) {
          const subSampleName: ISubSampleDetail = {
            subSampleFieldId: subSampleDefId,
            sampleName: pairId
          }
          const sampleId = SubSampleNameUtility.getSampleNameWithFieldId(subSampleName, subSampleNameSeparator)
          const settings = omitEmptyArrays({
            ...state.physicalConfigValues,
            ...state.logicalConfigValues,
            ...perSampleSettings[subSampleDefId]
          });
          sampleSettings.push({sampleId, settings});
        }
      }
    } else {
      // collect unique sampleName as sampleId from table 
      const uniqueSampleIds: string[] = state.sampleData ? uniq(state.sampleData.map(x => x.sampleName).filter(x => !isEmpty(x))) : [];
      sampleSettings = uniqueSampleIds.map(sampleId => {
        // include perSampleSettingsValues matching the sampleId only (only one sampleSettingValues allowed for one sampleId in config)
        const matchingPerSampleSettingsData = state.perSampleSettingsData
          ? state.perSampleSettingsData.find(x => x.sampleId === sampleId)
          : null;

        // physicalConfig & logicalConfig values are duplicated across all sampleIds within the same config
        const settings = omitEmptyArrays({
          ...state.physicalConfigValues,
          ...state.logicalConfigValues,
          ...(matchingPerSampleSettingsData && matchingPerSampleSettingsData.perSampleSettings)
        });
        return {sampleId, settings};
      });
    }

    return {
      name: getConfigTitle(state),
      analysisVersionDefinitionId: state.analysisVersionDefinitionId,
      description: state.analysisDescription,
      settings: omitEmptyArrays(state.analysisSettingsFormValues),
      sampleSettings,
      libraryPrepKitId: state.libraryPrepKitId === UNSPECIFIED_LIB_PREP_KIT.id ? null : state.libraryPrepKitId,
      indexAdapterKitId: state.indexAdapterKitId === UNSPECIFIED_INDEX_ADAPTER_KIT.id ? null : state.indexAdapterKitId,
    };
  }

  /**
   * Generates the request model for implicit BclConvert analysis config, based on the run planning state
   * with selected implicit BclConvert AVD and values. The sampleSettings of the config is always an empty
   * array given that the config is a global one that applies to all samples.
   * @param state state containing the implicit bclConvert AVD and form values
   */
  public static fromRunPlanning(state: RunPlanningState): gss.CreateSequencingRunAnalysisConfigurationRequest {
    const implicitBclSettingFields = get(state, 'implicitBclConvertAvd.analysisSettings.fields', []).map(x => x.id);
    const bclConvertFormValues = pick(state.implicitBclConvertFormValues, implicitBclSettingFields);
    return {
      name: IMPLICIT_BCL_CONVERT_CONFIG_NAME,
      analysisVersionDefinitionId: state.implicitBclConvertAvd.id,
      settings: omitEmptyArrays(bclConvertFormValues),
      sampleSettings: []
    };
  }
}
